import { useEffect, useState } from "react";

import {
  type CloudFlowNodeType,
  type CloudflowTriggerEvent,
  type NodeConfigSystemEventTriggerParameters,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import { type ModelData } from "@doitintl/models-any";
import { Button, Card, CircularProgress, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";

import DoitLogo from "../../../../../../assets/doit-logo-hero-square.svg";
import { cloudflowTexts } from "../../../../../../assets/texts";
import { useErrorSnackbar } from "../../../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useModalManager } from "../../../Common/CloudflowModalsProvider";
import { useCloudflowOperations } from "../../../Common/CloudflowOperationsProvider";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { useErrorUpdates } from "../hooks";
import ModelDescriptorTableStructure from "../ModelDescriptorTableStructure";
import { NodeCardHeader } from "../NodeCardHeader";
import { useSystemEventModel } from "./useSystemEventModel";
import { useSystemEvents } from "./useSystemEvents";

const getEventParametersFromEvent = (
  event: ModelData<CloudflowTriggerEvent, "id"> | undefined
): NodeConfigSystemEventTriggerParameters => ({
  event: event?.id ?? "",
  source: "systemEvents",
  version: event?.latestVersionRef.id ?? "",
});

export const SystemEventTriggerTab = () => {
  const { httpOperationLoading } = useCloudflowOperations();
  const { openModal } = useModalManager();
  const { updateNode, nodeConfig } = useNodeConfigurationContext<CloudFlowNodeType.SYSTEM_EVENT_TRIGGER>();
  const showErrorSnackbar = useErrorSnackbar();

  const [eventTouched, setEventTouched] = useState(false);
  const [events, isLoading, eventError] = useSystemEvents();
  const [model, isModelLoading, modelError] = useSystemEventModel(nodeConfig.parameters);

  useEffect(() => {
    if (eventError) showErrorSnackbar("Error loading DCI system events");
    if (modelError) showErrorSnackbar("Error loading DCI system events");
  }, [eventError, modelError, showErrorSnackbar]);

  useErrorUpdates({
    errorKey: "param_error",
    errorMessage: "Invalid Params",
    isValid: Boolean(nodeConfig.parameters?.event),
  });

  const isSelectError = !nodeConfig.parameters.event;

  return (
    <Stack
      sx={{
        p: 2,
        justifyContent: "center",
        gap: 2,
      }}
    >
      <Card sx={{ "&:hover": { borderColor: "primary.main" } }}>
        <NodeCardHeader
          avatar={<Box component="img" src={DoitLogo} sx={{ width: 24, height: 24 }} />}
          subheader="Start a flow when an event happens"
          title={cloudflowTexts.SYSTEM_EVENT}
          action={
            <Button
              disabled={httpOperationLoading}
              onClick={() => {
                openModal("trigger");
              }}
            >
              Change
            </Button>
          }
        />
      </Card>
      <Stack>
        <Typography variant="subtitle2">Select an event</Typography>
        <Typography variant="body2" color="textSecondary">
          When an instance of this event occurs it will trigger this CloudFlow to run
        </Typography>
      </Stack>
      <TextField
        label="Event"
        variant="outlined"
        select
        required
        error={eventTouched && isSelectError}
        value={nodeConfig.parameters?.event ?? ""}
        onChange={(e) => {
          setEventTouched(true);
          updateNode((prev) => ({
            ...prev,
            parameters: getEventParametersFromEvent(events?.find((ev) => ev.id === e.target.value)),
          }));
        }}
        slotProps={{
          input: {
            endAdornment: isLoading ? (
              <InputAdornment position="end" sx={{ marginRight: "28px" }}>
                <CircularProgress size={20} color="inherit" />
              </InputAdornment>
            ) : null,
          },
        }}
      >
        {isLoading ? (
          <MenuItem disabled>
            <Typography variant="body1" color="textPrimary">
              Loading...
            </Typography>
          </MenuItem>
        ) : (
          events?.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              <Typography variant="body1" color="textPrimary">
                {e.name}
              </Typography>
            </MenuItem>
          ))
        )}
      </TextField>
      <Stack>
        <Typography variant="subtitle2">Event payload details</Typography>
        <Typography variant="body2" color="text.secondary">
          These are the fields within your event that you will be able to reference in your CloudFlow
        </Typography>
      </Stack>

      <ModelDescriptorTableStructure
        model={(model ?? null) as UnwrappedApiServiceModelDescriptor | null}
        noRowsText="Select an event to see the payload details"
        // NOTE: have to do this due to a state issue with the useDocumentData hook
        loading={isModelLoading && Boolean(nodeConfig.parameters.event)}
      />
    </Stack>
  );
};
